/**
 * The WEEKLY canvas as one mountable unit: the overflow banner, the seven columns, and the move
 * plumbing behind them.
 *
 * It exists because the builder now has two canvases and each owns a Pragmatic **monitor**. A
 * monitor is bound for as long as its hook is mounted and every monitor whose `canMonitor` accepts a
 * source sees that source's drop — so calling `useWeekMove` and `useTapeMove` side by side in the
 * screen would dispatch a single drop TWICE, once as a weekday move and once as a positional one.
 * Splitting the canvases into components the screen mounts one of is what makes "exactly one move
 * mechanic is live at a time" structural rather than a rule somebody has to remember.
 *
 * ── The overflow banner is inside this component and OUTSIDE the scroller ────────────────────
 * Both halves are load-bearing and both were found by driving the screen. Inside the scroller it
 * scrolled out of view on exactly the weeks tall enough to hide a card. And it is ALWAYS rendered,
 * carrying an empty string when there is nothing to report, because a `role="status"` inserted
 * together with its text is not reliably announced — the region has to be in the accessibility tree
 * before the change happens. The empty state is styled away, never `display: none`, which would take
 * the region back out of the tree.
 *
 * The overflow it reports is no longer reached by a mode flip — that toggle was removed on 16
 * August 2026 when R1 fixed a plan's mode at creation. What still reaches it is a phase carrying two
 * days on the same weekday, or a day carrying none at all: the phone authors both, and the
 * calendar's day editor can add a second row to a weekday that already has one. The banner is how
 * the trainer is told, rather than left to notice a day that renders no card.

 * ── The DAY ACTIONS live here too, for the same reason the move does ─────────────────────────
 * Duplicating and deleting a workout are dispatched from the card's ⋯, they announce through the
 * canvas's own live region, and the deletion's confirm has to exist exactly once on screen. Holding
 * `useDayActions` here rather than in the screen gets all three for free: one canvas is mounted, so
 * one live region and one dialog, and neither has to be threaded down from a component that has no
 * live region of its own.
 *
 * ── And so does REVEALING the day the panel opened on ────────────────────────────────────────
 * The 574px panel overlays this canvas's right-hand edge, and this canvas is the one that scrolls
 * sideways. `useRevealOpenDay` therefore belongs here for the third time on the same argument: the
 * pane below is the scroller and must not learn that a panel exists, and the screen above mounts one
 * of two canvases and would have to ask which. See that hook's header for why the measurement is
 * `offsetWidth` and never a rect.
 */

import { useScreenMotionPreference } from '~/shared/design-system/motion';

import { useDayActions } from '../lib/useDayActions';
import { useRevealOpenDay } from '../lib/useRevealOpenDay';
import { useWeekMove } from '../lib/useWeekMove';
import { useMovedCardFocus } from '../lib/useMovedCardFocus';
import type { DayDockMode } from '../lib/usePlanBuilderSearch';
import { overlayRoom } from '../model/revealDay';
import type { WeekViewModel } from '../model/weekViewModel';

import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import { MOVE_HINT_ID, WeekPane } from './WeekPane';
import styles from './PlanBuilderScreen.module.css';

interface WeekCanvasProps {
  readonly week: WeekViewModel;
  /** The tab panel's id and the tab that labels it — the phase band owns both. */
  readonly panelId: string;
  readonly labelledBy: string;
  readonly onRenameSession: ((slotToken: string, name: string) => void) | null;
  readonly onProgramDay: ((slotToken: string) => void) | null;
  /** Create a day on an empty weekday and open the dock on it. `null` when read-only. */
  readonly onAddDay: ((weekday: number) => void) | null;
  readonly returnFocusToken: string | null;
  /**
   * Which day the dock is open on and in which host — the signal to reveal the column under the
   * panel. `null` when the dock is closed.
   *
   * It lives here rather than in `WeekPane`, which is the scroller and must not learn that a panel
   * exists, and rather than in `PlanBuilderScreen`, which mounts one of two canvases and would then
   * be reaching into whichever one it happened to draw. Exactly one canvas is mounted and this is
   * that canvas's own concern — the same argument that puts `useWeekMove` here.
   */
  readonly openDay: { readonly slotToken: string; readonly dock: DayDockMode } | null;
  /** The Buddy panel is open over the canvas's right-hand end (owner, 7 October 2026). */
  readonly assistantOpen: boolean;
  readonly addDayLabel: string;
  /** The phase in view. Forwarded so the pane can replay the handoff's fade when it changes. */
  readonly phaseIndex: number;
  /** False on a plan that cannot be edited — no grip, no ⋯, no drop targets. */
  readonly editable: boolean;
}

export function WeekCanvas({
  week,
  panelId,
  labelledBy,
  onRenameSession,
  onProgramDay,
  onAddDay,
  returnFocusToken,
  openDay,
  addDayLabel,
  phaseIndex,
  editable,
  assistantOpen,
}: WeekCanvasProps) {
  // The weekday table has to come from somewhere the move can read after a drop, and the view model
  // already holds it in column order — so the labels are lifted off it rather than formatted twice.
  const move = useWeekMove(week.columns.map((column) => column.weekdayLabel));
  /* The dock's returning card and the one a move just carried to another column, merged — see
   * `useMovedCardFocus` for why a committed move owes focus at all. */
  const focusToken = useMovedCardFocus(returnFocusToken, move);
  // Focus goes back to the PANEL after a confirmed deletion: the card's ⋯ that opened the dialog no
  // longer exists by the time Base UI would restore focus to it, so without this it lands on
  // `<body>`. The panel is the thing that changed and it is already a tab stop.
  const actions = useDayActions(move.announce, () => {
    document.getElementById(panelId)?.focus();
  });

  // The panel overlays the week's right-hand 574px, so a day opened under it would be invisible with
  // its selection ring. The preference is read here and passed IN — a scripted scroll cannot be
  // reached by the one `prefers-reduced-motion` block in `tokens/base.css`.
  const reducedMotion = useScreenMotionPreference();
  useRevealOpenDay({
    daySlotToken: openDay?.slotToken ?? null,
    dock: openDay?.dock ?? null,
    reducedMotion,
  });

  return (
    <>
      <p
        className={`${String(styles.overflow)} ${
          week.overflow == null ? String(styles.overflowEmpty) : ''
        }`}
        role="status"
      >
        {week.overflow ?? ''}
      </p>
      <WeekPane
        id={panelId}
        columns={week.columns}
        labelledBy={labelledBy}
        onRenameSession={onRenameSession}
        onProgramDay={onProgramDay}
        onAddDay={onAddDay}
        returnFocusToken={focusToken}
        addDayLabel={addDayLabel}
        addDayRefusal={week.chrome.addDayRefusal}
        phaseIndex={phaseIndex}
        // Only the PANELS cover the week: the day panel, the Buddy panel, or both. Fullscreen hides
        // everything behind a modal host, so there is no column to scroll clear of there.
        panelRoom={overlayRoom(openDay?.dock === 'panel', assistantOpen)}
        /* The mock's `active`: the column whose panel is open takes an accent edge and a 3px ember
         * ring. It is the DAY that is selected rather than the weekday, so the token travels — a day
         * moved while its panel is open keeps its ring. */
        openDaySlotToken={openDay?.slotToken ?? null}
        moveHint={week.chrome.moveHint}
        announcement={move.announcement}
        pendingTarget={move.pendingTarget}
        actions={editable ? { view: week.chrome.dayActions, ...actions } : null}
        // One object, or `null` — the card either can be moved and needs all of it, or the plan is
        // read-only and renders no grip and no ⋯ at all rather than two controls that refuse.
        move={
          editable
            ? {
                hintId: MOVE_HINT_ID,
                sourceLabel: week.chrome.moveSourceLabel,
                cancelledMessage: week.chrome.moveCancelled,
                moveDay: move.moveDay,
                announce: move.announce,
                setPendingTarget: move.setPendingTarget,
              }
            : null
        }
      />
      {/* One canvas is mounted at a time, so this is the ONE delete confirm on screen. It is
        * `null` until a card asks for it, which is what keeps a week of seven cards from mounting
        * seven dialog roots competing for one portal and one focus trap. */}
      <ConfirmDialog
        prompt={actions.prompt}
        onConfirm={actions.confirmDelete}
        onCancel={actions.cancelDelete}
      />
    </>
  );
}
